import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { refreshClientAvatars } from "@/components/ClientAvatar";
import { runAryeoSync } from "@/lib/sync";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2, XCircle, Loader2, KeyRound, RefreshCw, Plug, Cloud, ExternalLink, Unlink, Info, Music,
} from "lucide-react";
import { toast } from "sonner";

const StatusPill = ({ connected }) => (
  connected ? (
    <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300" data-testid="connection-status">
      <CheckCircle2 className="h-3.5 w-3.5" /> Connected
    </span>
  ) : (
    <span className="flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-xs font-medium text-rose-300" data-testid="connection-status">
      <XCircle className="h-3.5 w-3.5" /> Not connected
    </span>
  )
);

export default function Integrations() {
  const { checkAuth } = useAuth();
  const [params, setParams] = useSearchParams();

  // Aryeo
  const [settings, setSettings] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Dropbox
  const [dbx, setDbx] = useState(null);
  const [appKey, setAppKey] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [savingDbx, setSavingDbx] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // Music folder (uses the Dropbox connection)
  const [musicLink, setMusicLink] = useState("");
  const [savingMusic, setSavingMusic] = useState(false);
  const [syncingMusic, setSyncingMusic] = useState(false);

  const redirectUri = `${window.location.origin}/api/dropbox/callback`;

  const loadAryeo = async () => {
    try {
      const { data } = await api.get("/settings/aryeo");
      setSettings(data);
      setBaseUrl(data.base_url || "");
    } catch (err) { toast.error(apiError(err)); }
  };
  const loadDbx = async () => {
    try {
      const { data } = await api.get("/dropbox/status");
      setDbx(data);
      setMusicLink(data.music_folder_link || "");
    } catch (err) { toast.error(apiError(err)); }
  };

  useEffect(() => { loadAryeo(); loadDbx(); }, []);

  useEffect(() => {
    const d = params.get("dropbox");
    if (d === "linked") { toast.success("Dropbox connected successfully"); loadDbx(); }
    else if (d === "error") toast.error("Dropbox connection failed. Check your app credentials & redirect URI.");
    if (d) { params.delete("dropbox"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAryeo = async () => {
    setSaving(true);
    try {
      const body = { base_url: baseUrl };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.put("/settings/aryeo", body);
      data.connected ? toast.success("Aryeo connected successfully") : toast.error(data.message || "Could not connect");
      setApiKey("");
      await loadAryeo();
      await checkAuth();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  const testAryeo = async () => {
    try {
      const { data } = await api.post("/settings/aryeo/test");
      data.connected ? toast.success("Connection OK") : toast.error(data.message);
    } catch (err) { toast.error(apiError(err)); }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const data = await runAryeoSync();
      refreshClientAvatars();
      const extras = [
        data.created ? `${data.created} new` : null,
        data.archived ? `${data.archived} archived` : null,
        data.removed ? `${data.removed} removed` : null,
      ].filter(Boolean).join(", ");
      toast.success(`Synced ${data.total} orders${extras ? ` (${extras})` : ""}`);
    } catch (err) { toast.error(apiError(err, "Sync failed")); }
    finally { setSyncing(false); }
  };

  const saveDbxCreds = async () => {
    if (!appKey.trim() || !appSecret.trim()) { toast.error("Enter both app key and app secret"); return; }
    setSavingDbx(true);
    try {
      await api.put("/dropbox/credentials", { app_key: appKey.trim(), app_secret: appSecret.trim() });
      setAppKey(""); setAppSecret("");
      await loadDbx();
      toast.success("Dropbox credentials saved. Now connect your account.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingDbx(false); }
  };
  const connectDbx = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get(`/dropbox/authorize-url?return_url=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err)); setConnecting(false); }
  };
  const disconnectDbx = async () => {
    try {
      await api.post("/dropbox/disconnect");
      await loadDbx();
      toast.success("Dropbox disconnected");
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveMusicFolder = async () => {
    setSavingMusic(true);
    try {
      await api.put("/music/folder", { link: musicLink.trim() });
      await loadDbx();
      toast.success(musicLink.trim() ? "Music folder saved" : "Music folder cleared");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingMusic(false); }
  };
  const syncMusic = async () => {
    setSyncingMusic(true);
    try {
      const { data } = await api.post("/music/sync");
      toast.success(`Music synced — ${data.total} tracks${data.added ? ` (${data.added} new)` : ""}`);
    } catch (err) { toast.error(apiError(err, "Music sync failed")); }
    finally { setSyncingMusic(false); }
  };

  return (
    <div>
      <PageHeader title="Integrations" subtitle="Connect the services that power your workflow" testid="integrations-header" />
      <div className="mx-auto max-w-2xl space-y-6 p-6 md:p-8">

        {/* Aryeo */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="aryeo-settings">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 ring-1 ring-primary/30">
              <Plug className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">Aryeo</h3>
              <p className="text-sm text-muted-foreground">Pull your orders, clients & deliverables from Aryeo.</p>
            </div>
            {settings && <StatusPill connected={settings.connected} />}
          </div>
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="apikey">API Key</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="apikey" data-testid="aryeo-key-input" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                  placeholder={settings?.connected ? `•••• •••• ${settings.key_last4}` : "Paste your Aryeo API token"}
                  className="pl-9" type="password" />
              </div>
              <p className="text-xs text-muted-foreground">Find this in Aryeo → Developer Settings → API keys.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="baseurl">API Base URL</Label>
              <Input id="baseurl" data-testid="aryeo-baseurl-input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.aryeo.com/v1" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={saveAryeo} disabled={saving} data-testid="save-aryeo-btn" className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save & Connect
              </Button>
              {settings?.connected && (
                <>
                  <Button onClick={testAryeo} variant="outline" data-testid="test-aryeo-btn" className="gap-2"><Plug className="h-4 w-4" /> Test</Button>
                  <Button onClick={sync} disabled={syncing} variant="outline" data-testid="settings-sync-btn" className="gap-2">
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now
                  </Button>
                </>
              )}
            </div>
            {settings?.connected && (
              <p className="text-xs text-muted-foreground">
                Sync pulls your 30 most recent orders plus every shoot in the last 10 days and upcoming. Past shoots drop out of your boards — delivered ones are archived (still counted in stats), and empty/never-delivered ones are removed.
              </p>
            )}
          </div>
        </div>

        {/* Dropbox */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="dropbox-settings">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/15 ring-1 ring-blue-500/30">
              <Cloud className="h-5 w-5 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">Dropbox</h3>
              <p className="text-sm text-muted-foreground">Auto-link each property to its Dropbox folder by address.</p>
            </div>
            {dbx && <StatusPill connected={dbx.connected} />}
          </div>

          {/* Setup guide */}
          <button onClick={() => setShowGuide((s) => !s)} data-testid="dropbox-guide-toggle"
            className="mt-5 flex w-full items-center gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-secondary/50">
            <Info className="h-4 w-4 text-blue-400" /> How to set up your Dropbox app
            <span className="ml-auto text-xs text-muted-foreground">{showGuide ? "Hide" : "Show"}</span>
          </button>
          {showGuide && (
            <div className="mt-2 space-y-2 rounded-lg border border-border bg-secondary/20 p-4 text-sm text-muted-foreground" data-testid="dropbox-guide">
              <ol className="list-decimal space-y-2 pl-4">
                <li>Go to the <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noreferrer" className="text-primary underline">Dropbox App Console</a> and click <b>Create app</b>.</li>
                <li>Choose <b>Scoped access</b> and <b>Full Dropbox</b> access, then name your app.</li>
                <li>On the <b>Permissions</b> tab, enable <code className="rounded bg-background px-1">files.metadata.read</code>, <code className="rounded bg-background px-1">sharing.read</code>, <code className="rounded bg-background px-1">sharing.write</code>, <code className="rounded bg-background px-1">account_info.read</code> and click <b>Submit</b>.</li>
                <li>On the <b>Settings</b> tab, under <b>OAuth 2 → Redirect URIs</b>, add this exact URL:
                  <div className="mt-1 flex items-center gap-2">
                    <code className="block flex-1 break-all rounded bg-background px-2 py-1 text-xs text-foreground" data-testid="dropbox-redirect-uri">{redirectUri}</code>
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Copied"); }}>Copy</Button>
                  </div>
                  <p className="mt-1 text-xs">This matches the address you're using now (the live app is <b>ops.homepixmedia.com</b>). If you also open the app from another domain, add that domain's <code className="rounded bg-background px-1">/api/dropbox/callback</code> URL too.</p>
                </li>
                <li>Copy the <b>App key</b> and <b>App secret</b> from the Settings tab and paste them below.</li>
              </ol>
            </div>
          )}

          {/* Credentials + connect */}
          <div className="mt-5 space-y-4">
            {!dbx?.connected && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="dbx-key">App key</Label>
                    <Input id="dbx-key" data-testid="dropbox-appkey-input" value={appKey} onChange={(e) => setAppKey(e.target.value)}
                      placeholder={dbx?.has_credentials ? `•••• ${dbx.app_key_last4}` : "Dropbox app key"} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dbx-secret">App secret</Label>
                    <Input id="dbx-secret" data-testid="dropbox-appsecret-input" type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)}
                      placeholder={dbx?.has_credentials ? "•••• (saved)" : "Dropbox app secret"} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={saveDbxCreds} disabled={savingDbx} variant="outline" data-testid="save-dropbox-creds-btn" className="gap-2">
                    {savingDbx ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Save credentials
                  </Button>
                  <Button onClick={connectDbx} disabled={connecting || !dbx?.has_credentials} data-testid="connect-dropbox-btn" className="gap-2">
                    {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />} Connect Dropbox
                  </Button>
                </div>
                {!dbx?.has_credentials && <p className="text-xs text-muted-foreground">Save your app key & secret first, then connect your account.</p>}
              </>
            )}
            {dbx?.connected && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium" data-testid="dropbox-account-name">{dbx.account_name || "Dropbox account"}</div>
                  {dbx.account_email && <div className="truncate text-xs text-muted-foreground">{dbx.account_email}</div>}
                </div>
                <Button onClick={disconnectDbx} variant="outline" data-testid="disconnect-dropbox-btn" className="gap-2 text-destructive hover:text-destructive">
                  <Unlink className="h-4 w-4" /> Disconnect
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Music Library folder */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="music-folder-settings">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-fuchsia-500/15 ring-1 ring-fuchsia-500/30">
              <Music className="h-5 w-5 text-fuchsia-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-bold tracking-tight">Music Library</h3>
              <p className="text-sm text-muted-foreground">Paste a Dropbox shared folder link that holds your background music tracks.</p>
            </div>
            {dbx && <StatusPill connected={!!dbx.music_folder_link} />}
          </div>

          <div className="mt-5 space-y-4">
            {!dbx?.connected && (
              <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-300" data-testid="music-needs-dropbox">
                Connect Dropbox above first — the music library streams files through your Dropbox connection.
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="music-link">Dropbox shared folder link</Label>
              <Input id="music-link" data-testid="music-folder-input" value={musicLink}
                onChange={(e) => setMusicLink(e.target.value)}
                placeholder="https://www.dropbox.com/scl/fo/…?rlkey=…" />
              <p className="text-xs text-muted-foreground">In Dropbox, right-click your music folder → <b>Share</b> → <b>Copy link</b>, then paste it here.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={saveMusicFolder} disabled={savingMusic} data-testid="save-music-folder-btn" className="gap-2">
                {savingMusic ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save folder
              </Button>
              {dbx?.music_folder_link && dbx?.connected && (
                <Button onClick={syncMusic} disabled={syncingMusic} variant="outline" data-testid="sync-music-btn" className="gap-2">
                  {syncingMusic ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync music now
                </Button>
              )}
            </div>
            {dbx?.music_folder_link && (
              <p className="text-xs text-muted-foreground">Manage & preview tracks on the <b>Music</b> page. Run “Sync music” whenever you add new files in Dropbox.</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
